/**
 * 智能体团队类型定义
 *
 * 定义智能体团队会话相关的TypeScript类型，包括会话管理、对话消息、智能体状态、任务清单和角色配置。
 */

// ==================== 枚举定义 ====================

/** 会话状态枚举 */
export const SESSION_STATUSES = [
  'planning',
  'reviewing',
  'executing',
  'completed',
  'failed',
  'cancelled',
] as const;

export type SessionStatus = typeof SESSION_STATUSES[number];

/** 对话角色枚举 */
export const CONVERSATION_ROLES = [
  'user',
  'orchestrator',
  'executor',
  'system',
  'planner',
] as const;

export type ConversationRole = typeof CONVERSATION_ROLES[number];

/** 消息类型枚举 */
export const MESSAGE_TYPES = ['text', 'task_list', 'agent_status'] as const;

export type MessageType = typeof MESSAGE_TYPES[number];

/** 智能体状态枚举 */
export const AGENT_STATUSES = [
  'idle',
  'thinking',
  'executing',
  'waiting',
  'completed',
  'retired',
] as const;

export type AgentStatus = typeof AGENT_STATUSES[number];

/** 任务类型枚举 */
export const TASK_TYPES = ['explore', 'write', 'research', 'analysis', 'general'] as const;

export type TaskType = typeof TASK_TYPES[number];

/** 任务状态枚举 */
export const TASK_STATUSES = ['pending', 'running', 'completed', 'failed', 'cancelled'] as const;

export type TaskStatus = typeof TASK_STATUSES[number];

// ==================== 会话相关接口 ====================

/** 智能体团队会话信息 */
export interface AgentTeamSession {
  id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  title: string;
  status: SessionStatus;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
}

/** 创建会话请求 */
export interface SessionCreateRequest {
  workspace_id: number;
  user_command: string;
}

/** 会话详情响应（包含对话历史） */
export interface SessionDetailResponse {
  session: AgentTeamSession;
  conversations: AgentConversationMessage[];
  conversation_count: number;
  total_count: number;
}

/** 会话列表响应 */
export interface SessionListResponse {
  sessions: AgentTeamSession[];
  total: number;
}

// ==================== 对话消息相关接口 ====================

/** 对话消息 */
export interface AgentConversationMessage {
  id: number;
  session_id: string;  // UUID format
  sequence_number: number;
  role: ConversationRole;
  speaker_name: string;
  agent_id: number | null;
  message_type: MessageType;
  content: string;
  data: Record<string, unknown> | null;
  is_final: boolean;
  created_at: string | null;
}

/** 发送消息请求 */
export interface SendMessageRequest {
  content: string;
}

/** SSE事件类型 */
export interface SSEEvent {
  event: string;
  data: unknown;
}

/** SSE消息块事件 */
export interface SSEChunkEvent {
  type: 'chunk';
  content: string;
}

/** SSE消息结束事件 */
export interface SSEEndEvent {
  type: 'end';
  message_id: number;
  full_content: string;
}

/** SSE错误事件 */
export interface SSEErrorEvent {
  type: 'error';
  message: string;
  code?: string;
}

// ==================== 智能体状态相关接口 ====================

/** 活跃智能体信息 */
export interface ActiveAgent {
  agent_id: number | null;
  agent_name: string;
  agent_role: 'planner' | 'supervisor' | 'executor';
  status: AgentStatus;
  task_id: number | null;
  task_description: string | null;
  task_type: TaskType | null;
  tools: string[];
  progress: number;
  last_update: string;
}

/** 智能体状态更新 */
export interface AgentStatusUpdate {
  session_id: string;  // UUID format
  agent_name: string;
  agent_role: ConversationRole;
  status: AgentStatus;
  task_id?: number;
  message?: string;
}

// ==================== 任务清单相关接口 ====================

/** 任务派发状态 */
export type TaskDispatchStatus = 'pending' | 'claimed' | 'running' | 'completed' | 'failed' | 'killed';

/** 任务派发类型 */
export type TaskDispatchType = 'research' | 'synthesis' | 'implementation' | 'verification' | 'custom';

/** 任务派发项 */
export interface TaskDispatchItem {
  id: number;
  session_id: string;
  agent_type: string;
  task_type: TaskDispatchType;
  description: string;
  status: TaskDispatchStatus;
  priority: string;
  dependencies: number[];
  context: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error: string | null;
  claimed_by: string | null;
  created_at: string | null;
  claimed_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

/** 任务项 */
export interface TaskItem {
  id: number | string;
  name: string;
  description: string;
  task_type: TaskType;
  assignee_name?: string;
  dependencies?: (number | string)[];
  status: TaskStatus;
  priority?: 'low' | 'medium' | 'high';
}

/** 任务清单 */
export interface TaskList {
  title: string;
  description?: string;
  tasks: TaskItem[];
  estimated_time?: string;
  approval_status?: 'pending' | 'approved' | 'rejected';
  objections?: string[];
}

/** 任务清单卡片数据 */
export interface TaskListCardData {
  session_id: string;  // UUID format
  task_list: TaskList;
  planner_name: string;
  supervisor_name: string;
  created_at: string;
}

// ==================== 角色配置相关接口 ====================

/** LLM配置 */
export interface LLMConfig {
  model?: string;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  stop?: string[];
}

/** 角色配置 */
export interface RoleConfig {
  id: number;
  workspace_id: number;
  role: 'planner' | 'supervisor';
  display_name: string;
  prompt: string;
  enabled_tools: string[];
  temperature: number;
  llm_config: LLMConfig | null;
  created_at: string | null;
  updated_at: string | null;
}

/** 配置更新请求 */
export interface ConfigUpdateRequest {
  role: 'planner' | 'supervisor';
  display_name?: string;
  prompt?: string;
  enabled_tools?: string[];
  temperature?: number;
  llm_config?: LLMConfig;
}

/** 配置重置请求 */
export interface ConfigResetRequest {
  role?: 'planner' | 'supervisor';
}

/** 配置列表响应 */
export interface ConfigListResponse {
  planner: RoleConfig | null;
  supervisor: RoleConfig | null;
}

// ==================== WebSocket 事件类型 ====================

/** 会话创建事件数据 */
export interface SessionCreatedEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  title: string;
  status: SessionStatus;
  planner_name: string;
  supervisor_name: string;
}

/** 智能体加入事件数据 */
export interface AgentJoinedEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  agent_name: string;
  agent_role: 'executor';
  task_id: number | string;
  task_description: string;
  task_type: TaskType;
  tools: string[];
}

/** 智能体离开事件数据 */
export interface AgentLeftEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  agent_name: string;
  agent_role: 'executor';
  reason: 'task_completed' | 'error' | 'terminated';
}

/** 智能体进度事件数据 */
export interface AgentProgressEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  agent_name: string;
  agent_role: 'executor';
  status: AgentStatus;
  message: string;
}

/** 任务清单就绪事件数据 */
export interface TaskListReadyEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  task_list: TaskList;
  task_count: number;
}

/** 状态变化事件数据 */
export interface StatusChangedEventData {
  session_id: string;  // UUID format
  workspace_id: number;
  user_id: number;
  old_status: SessionStatus;
  new_status: SessionStatus;
}

/** 智能体团队 WebSocket 事件 */
export interface AgentTeamWebSocketEvent {
  type: string;
  session_id: string;
  data:
    | SessionCreatedEventData
    | AgentJoinedEventData
    | AgentLeftEventData
    | AgentProgressEventData
    | TaskListReadyEventData
    | StatusChangedEventData;
  timestamp: string;
}

// ==================== 工作流阶段相关接口 ====================

/** 工作流阶段名称 */
export const WORKFLOW_STAGES = ['research', 'synthesis', 'implementation', 'verification'] as const;
export type WorkflowStageName = typeof WORKFLOW_STAGES[number];

/** 工作流阶段标签 */
export const STAGE_LABELS: Record<WorkflowStageName, string> = {
  research: '调研',
  synthesis: '综合',
  implementation: '实施',
  verification: '验证',
};

/** 工作流阶段状态 */
export interface WorkflowStageState {
  stage: WorkflowStageName;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
}

// ==================== 工具常量 ====================

/** 写入工具集合 */
export const WRITE_TOOLS = new Set([
  'write_chapter',
  'edit_chapter',
  'create_chapter',
  'delete_chapter',
]);

/** 读取工具集合 */
export const READ_TOOLS = new Set([
  'read_chapter',
  'read_volume',
  'read_document',
  'list_chapters',
]);

/** 网络工具集合 */
export const WEB_TOOLS = new Set(['search_web', 'web_search']);

/** 所有工具列表 */
export const ALL_TOOLS = [
  ...READ_TOOLS,
  ...WEB_TOOLS,
  ...WRITE_TOOLS,
  'subagent',
  'skill',
];

/** 工具分类 */
export const TOOL_CATEGORIES = {
  read: {
    name: '读取工具',
    tools: [...READ_TOOLS],
    description: '用于读取章节、卷册、文档等内容的工具',
  },
  web: {
    name: '网络工具',
    tools: [...WEB_TOOLS],
    description: '用于网络搜索和资料查询的工具',
  },
  write: {
    name: '写入工具',
    tools: [...WRITE_TOOLS],
    description: '用于创建、编辑、删除章节的工具',
  },
  other: {
    name: '其他工具',
    tools: ['subagent', 'skill'],
    description: '调用子智能体或技能的工具',
  },
} as const;

// ==================== 工具函数 ====================

/**
 * 判断会话状态是否为终态
 */
export function isTerminalSessionStatus(status: SessionStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/**
 * 判断智能体状态是否为活跃状态
 */
export function isActiveAgentStatus(status: AgentStatus): boolean {
  return status === 'thinking' || status === 'executing' || status === 'waiting';
}

/**
 * 获取角色显示名称
 */
export function getRoleDisplayName(role: ConversationRole): string {
  const roleNames: Record<ConversationRole, string> = {
    user: '用户',
    orchestrator: '编排者',
    executor: '执行者',
    system: '系统',
    planner: '规划者',
  };
  return roleNames[role];
}

/**
 * 获取状态显示名称
 */
export function getStatusDisplayName(status: SessionStatus): string {
  const statusNames: Record<SessionStatus, string> = {
    planning: '规划中',
    reviewing: '审查中',
    executing: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消',
  };
  return statusNames[status];
}